export interface CurrencyFormatOptions {
  currency?: string;
  decimals?: number;
  fallback?: string;
}

export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'CA$',
  AUD: 'A$',
  JPY: '¥',
};

const COMMON_CURRENCY_SYMBOLS = CURRENCY_SYMBOLS;

/**
 * Parameterized currency formatter supporting standard ISO codes, custom symbols,
 * precision control, and missing-value fallback handling.
 *
 * Compatible with both:
 * - formatCurrency(amount, { currency: 'USD', decimals: 0 })
 * - formatCurrency(amount, 'USD', 0)
 */
export function formatCurrency(
  amount?: number | null,
  currencyOrOptions?: string | CurrencyFormatOptions,
  legacyDecimals?: number
): string {
  let currency = 'USD';
  let decimals: number | undefined;
  let fallback: string | undefined;

  if (typeof currencyOrOptions === 'string') {
    currency = currencyOrOptions;
    decimals = legacyDecimals;
  } else if (currencyOrOptions && typeof currencyOrOptions === 'object') {
    currency = currencyOrOptions.currency ?? 'USD';
    decimals = currencyOrOptions.decimals;
    fallback = currencyOrOptions.fallback;
  }

  if (amount == null || !Number.isFinite(amount)) {
    return fallback ?? '';
  }

  try {
    const opts: Intl.NumberFormatOptions = {
      style: 'currency',
      currency,
    };
    if (decimals !== undefined) {
      opts.minimumFractionDigits = decimals;
      opts.maximumFractionDigits = decimals;
    }
    return new Intl.NumberFormat('en-US', opts).format(amount);
  } catch {
    // Fallback for non-standard currency codes
    const symbol = (nonEmptyString(COMMON_CURRENCY_SYMBOLS[currency]) ?? `${currency} `);
    const numStr = decimals !== undefined ? amount.toFixed(decimals) : amount.toLocaleString('en-US');
    return `${symbol}${numStr}`;
  }
}

/**
 * Convenience helper formatting USD currency with zero decimals by default, e.g. "$1,250".
 */
export function formatMoney(amount?: number | null, decimals = 0): string {
  return formatCurrency(amount, { currency: 'USD', decimals });
}

/**
 * Formats an ISO date string as a short readable date, e.g. "Oct 12, 2026".
 * Returns fallback if date is missing or invalid.
 */
export function formatShortDate(isoDate?: string | null, fallback = 'N/A'): string {
  if (!isoDate) return fallback;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(isoDate);
  const d = new Date(dateOnly ? `${isoDate}T12:00:00` : isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Splits a date string into short month / day / weekday labels,
 * used by the upcoming-deadlines list to render a compact calendar-style chip.
 */
export const formatDateParts = (dateStr: string): { month: string; day: string; weekday: string; } => {
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr}T12:00:00` : dateStr);
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const day = d.toLocaleDateString('en-US', { day: 'numeric' });
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
  return { month, day, weekday };
};
/** Empty text is missing for display fallbacks; whitespace is deliberately preserved. */
export function nonEmptyString<T extends string>(value: T | null | undefined): T | undefined {
  return value === '' ? undefined : value ?? undefined;
}

/** Numeric defaults that intentionally treat zero and NaN as missing. */
export function nonZeroNumber(value: number | null | undefined): number | undefined {
  return value === 0 || (value != null && Number.isNaN(value)) ? undefined : value ?? undefined;
}

/** Used by event handlers to initialize date fields at the time of the action. */
export function dateDaysFromNow(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString().split('T')[0] ?? '';
}
